import React, { useState, useEffect } from 'react';
import { Heart, Gift, Users, Edit, Save, X, Copy, Check, Mail, Info, CircleCheckBig } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import zelleLogo from '../assets/zelle-logo.png';
import venmoLogo from '../assets/venmo-logo.png';
import { ReactComponent as WhatsappIcon } from '../assets/whatsapp-icon.svg'

const Donate = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState('success');
  const [copiedField, setCopiedField] = useState(null);
  const [showToast, setShowToast] = useState(false);

  // Payment information - loaded from database
  const [paymentInfo, setPaymentInfo] = useState({
    zelleEmail: '',
    venmoNumber: '',
    whatsapp: '',
    email: 'donations@stmarysorthodox.org'
  });

  const [paymentForm, setPaymentForm] = useState({
    zelleEmail: '',
    venmoNumber: '',
    whatsapp: '',
    email: ''
  });

  const [showWhatsappTooltip, setShowWhatsappTooltip] = useState(false);
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);

  useEffect(() => {
    fetchPaymentInfo();
  }, []);

  const fetchPaymentInfo = async () => {
    try {
      console.log('Fetching payment info from database...');
      const response = await api.get('/api/payment/info');
      console.log('Payment info response:', response.data);
      
      const paymentData = {
        zelleEmail: response.data.zelleEmail || '',
        venmoNumber: response.data.venmoNumber || '',
        whatsapp: response.data.whatsapp || '',
        email: response.data.email || 'donations@stmarysorthodox.org'
      };
      setPaymentInfo(paymentData);
      setPaymentForm(paymentData);
    } catch (error) {
      console.error('Error fetching payment info:', error);
      // If no payment info exists (404), set defaults
      if (error.response?.status === 404) {
        const defaultPayment = {
          zelleEmail: '',
          venmoNumber: '',
          whatsapp: '',
          email: 'donations@stmarysorthodox.org'
        };
        setPaymentInfo(defaultPayment);
        setPaymentForm(defaultPayment);
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const showCustomDialog = (message, type = 'success') => {
    setDialogMessage(message);
    setDialogType(type);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setDialogMessage('');
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSavePaymentInfo = async () => {
    // Validate payment information - only email is required
    if (!paymentForm.email.trim()) {
      showCustomDialog('Contact email is required', 'error');
      return;
    }

    // Warning if no payment methods are provided
    if (!paymentForm.zelleEmail.trim() && !paymentForm.venmoNumber.trim()) {
      console.log('Warning: No payment methods provided - payment will be marked as down');
    }

    setLoading(true);
    try {
      console.log('Saving payment info...');
      console.log('Payment form:', paymentForm);

      const response = await api.put('/api/payment/info', {
        zelleEmail: paymentForm.zelleEmail.trim() || null,
        venmoNumber: paymentForm.venmoNumber.trim() || null,
        whatsapp: paymentForm.whatsapp.trim() || null,
        email: paymentForm.email.trim()
      });
      
      console.log('Save response:', response.data);
      
      const paymentData = {
        zelleEmail: response.data.zelleEmail || '',
        venmoNumber: response.data.venmoNumber || '',
        whatsapp: response.data.whatsapp || '',
        email: response.data.email || 'donations@stmarysorthodox.org'
      };
      setPaymentInfo(paymentData);
      
      setIsEditingPayment(false);
      
      // Show appropriate message based on payment methods availability
      if (!paymentData.zelleEmail && !paymentData.venmoNumber) {
        showCustomDialog('Payment information updated successfully! Note: Payment services are currently disabled.');
      } else {
        showCustomDialog('Payment information updated successfully!');
      }
    } catch (error) {
      console.error('Error saving payment info:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save changes. Please try again.';
      showCustomDialog(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingPayment(false);
    // Reset payment form to current saved values
    setPaymentForm(paymentInfo);
  };

  const handleCopyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setShowToast(true);
      setTimeout(() => setCopiedField(null), 2000);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  function handleBlur(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setShowWhatsappTooltip(false);
      setShowEmailTooltip(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-r from-red-800 to-red-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Support Our Ministry</h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed">
            Your generous donations help us continue our mission of serving God and our community.
          </p>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Make a Donation</h2>
            
            {/* Payment Methods Header */}
            <div className="text-center mb-8">
              {!initialLoading && paymentInfo.zelleEmail && paymentInfo.venmoNumber &&(
                <h3 className="text-xl font-semibold text-gray-800 mb-6">
                  Choose any one payment method to make your donation
                </h3>
              )}
              
              {/* Admin Controls */}
              {user && (
                <div className="flex justify-center mb-6">
                  <button
                    onClick={() => setIsEditingPayment(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Payment Info
                  </button>
                </div>
              )}
            </div>

            {/* Payment Methods Display */}
            <div className="mb-8">
              {initialLoading ? (
                <div className="bg-gray-100 p-12 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">Loading Payment Information...</h3>
                    <p className="text-gray-500">Please wait while we fetch the donation details.</p>
                  </div>
                </div>
              ) : !paymentInfo.zelleEmail && !paymentInfo.venmoNumber ? (
                // No payment methods available
                <div className="bg-gray-100 p-12 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-red-600 mb-2">Payment is currently down</h3>
                    <p className="text-gray-600">We're working to restore payment services. Please check back later.</p>
                  </div>
                </div>
              ) : paymentInfo.zelleEmail && paymentInfo.venmoNumber ? (
                // Both payment methods available
                <div className="relative">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Zelle Section */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-[1.2rem] rounded-lg border-2 border-purple-200">
                      <div className="text-center">
                        <div className="flex items-center justify-center mx-auto mb-4">
                          <img 
                            src={zelleLogo} 
                            alt="Zelle Logo" 
                            className="h-16 w-auto object-contain"
                          />
                        </div>
                        <p className="text-purple-700 mb-4">Send money to:</p>
                        <div className="bg-white p-4 rounded-lg border border-purple-300 flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-800 sm:text-sm flex-1 break-all">{paymentInfo.zelleEmail}</p>
                          <button
                            onClick={() => handleCopyToClipboard(paymentInfo.zelleEmail, 'zelle')}
                            className="ml-3 p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
                            title="Copy email address"
                          >
                            {copiedField === 'zelle' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Venmo Section */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-[1.2rem] rounded-lg border-2 border-blue-200">
                      <div className="text-center">
                        <div className="flex items-center justify-center mx-auto mb-4">
                          <img 
                            src={venmoLogo} 
                            alt="Venmo Logo" 
                            className="h-16 w-auto object-contain"
                          />
                        </div>
                        <p className="text-blue-700 mb-4">Send money to:</p>
                        <div className="bg-white p-4 rounded-lg border border-blue-300 flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-800 sm:text-sm flex-1 break-all">{paymentInfo.venmoNumber}</p>
                          <button
                            onClick={() => handleCopyToClipboard(paymentInfo.venmoNumber, 'venmo')}
                            className="ml-3 p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                            title="Copy Venmo number"
                          >
                            {copiedField === 'venmo' ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Vertical Divider */}
                  <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>
                </div>
              ) : (
                // Only one payment method available
                <div className="max-w-md mx-auto">
                  {paymentInfo.zelleEmail && (
                    <div className="mb-6">
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-[1.2rem] rounded-lg border-2 border-purple-200">
                        <div className="text-center">
                          <div className="flex items-center justify-center mx-auto mb-4">
                            <img 
                              src={zelleLogo} 
                              alt="Zelle Logo" 
                              className="h-16 w-auto object-contain"
                            />
                          </div>
                          <p className="text-purple-700 mb-4">Send money directly to:</p>
                          <div className="bg-white p-4 rounded-lg border border-purple-300 flex items-center justify-between">
                            <p className="text-lg font-semibold text-gray-800 text-sm flex-1 break-all">{paymentInfo.zelleEmail}</p>
                            <button
                              onClick={() => handleCopyToClipboard(paymentInfo.zelleEmail, 'zelle-single')}
                              className="ml-3 p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
                              title="Copy email address"
                            >
                              {copiedField === 'zelle-single' ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      {!paymentInfo.venmoNumber && (
                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-sm text-yellow-800 text-center">
                            <strong>Venmo payments are currently down.</strong> We'll be back soon.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {paymentInfo.venmoNumber && (
                    <div className="mb-6">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-[1.2rem] rounded-lg border-2 border-blue-200">
                        <div className="text-center">
                          <div className="flex items-center justify-center mx-auto mb-4">
                            <img 
                              src={venmoLogo} 
                              alt="Venmo Logo" 
                              className="h-16 w-auto object-contain"
                            />
                          </div>
                          <p className="text-blue-700 mb-4">Send money to:</p>
                          <div className="bg-white p-4 rounded-lg border border-blue-300 flex items-center justify-between">
                            <p className="text-lg font-semibold text-gray-800 text-sm flex-1 break-all">{paymentInfo.venmoNumber}</p>
                            <button
                              onClick={() => handleCopyToClipboard(paymentInfo.venmoNumber, 'venmo-single')}
                              className="ml-3 p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                              title="Copy Venmo number"
                            >
                              {copiedField === 'venmo-single' ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      {!paymentInfo.zelleEmail && (
                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-sm text-yellow-800 text-center">
                            <strong>Zelle payments are currently down.</strong> We'll be back soon.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Edit Payment Info Modal */}
            {isEditingPayment && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-auto max-h-[97%] overflow-y-auto">
                  <h3 className="text-xl font-bold mb-6">Edit Payment Information</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> You can leave payment method fields empty if they're not available. 
                        If both are empty, the page will show "Payment is currently down" to visitors.
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zelle Email Address
                      </label>
                      <input
                        type="email"
                        name="zelleEmail"
                        value={paymentForm.zelleEmail}
                        onChange={handlePaymentFormChange}
                        placeholder="donations@church.org (optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Email address for Zelle payments (leave empty if not available)
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Venmo Username/Number
                      </label>
                      <input
                        type="text"
                        name="venmoNumber"
                        value={paymentForm.venmoNumber}
                        onChange={handlePaymentFormChange}
                        placeholder="@username or phone number (optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Venmo username (with @) or phone number (leave empty if not available)
                      </p>
                    </div>
                    
                    <div className="border-t pt-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Payment Confirmation Contact</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Contact details where donors should send payment screenshots for confirmation.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            WhatsApp Number
                          </label>
                          <input
                            type="text"
                            name="whatsapp"
                            value={paymentForm.whatsapp}
                            onChange={handlePaymentFormChange}
                            placeholder="+1-XXX-XXX-XXXX (optional)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Include country code (e.g., +1-555-123-4567) - optional
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={paymentForm.email}
                            onChange={handlePaymentFormChange}
                            placeholder="donations@church.org"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Email where donors can send payment confirmations
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={handleSavePaymentInfo}
                      disabled={loading || !paymentForm.email.trim()}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Payment Info
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors flex items-center justify-center"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Confirmation Instructions */}
            {!initialLoading && (paymentInfo.zelleEmail || paymentInfo.venmoNumber) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-[1.2rem] mb-8">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                  <CircleCheckBig className="w-5 h-5 mr-2" />
                  After payment is completed, please share the payment screenshot using any of the following options:
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {paymentInfo.whatsapp && (
                    <div className="bg-white p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center mb-2">
                        <WhatsappIcon className="mr-2" />
                        <span className="font-semibold text-gray-900">WhatsApp</span>
                        <span
                          className="relative"
                          onMouseEnter={() => setShowWhatsappTooltip(true)}
                          onMouseLeave={() => setShowWhatsappTooltip(false)}
                          onClick={() => setShowWhatsappTooltip((v) => !v)}
                          tabIndex={0}
                          onBlur={handleBlur}
                          style={{ outline: "none" }}
                        >
                        <Info
                          className="w-3 h-3 text-blue-500 ml-[4px] mt-[2px] cursor-pointer"
                          aria-label="Help"
                        />
                        {showWhatsappTooltip && (
                          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 bg-gray-700 text-white text-xs rounded px-3 py-2 shadow-lg z-10 w-[165px]">
                            Click the number below to start a WhatsApp chat.
                          </div>
                        )}
                        </span>
                      </div>
                      <a
                        href={`https://wa.me/${paymentInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 font-medium break-all"
                      >
                        {paymentInfo.whatsapp}
                      </a>
                    </div>
                  )}
                  
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center mb-2">
                      <Mail className="w-5 h-5 text-red-600 mr-2" />
                      <span className="font-semibold text-gray-900">Email</span>
                      <span
                        className="relative"
                        onMouseEnter={() => setShowEmailTooltip(true)}
                        onMouseLeave={() => setShowEmailTooltip(false)}
                        onClick={() => setShowEmailTooltip((v) => !v)}
                        tabIndex={0}
                        onBlur={handleBlur}
                        style={{ outline: "none" }}
                      >
                      <Info
                        className="w-3 h-3 text-blue-500 ml-[4px] mt-[2px] cursor-pointer"
                        aria-label="Help"
                      />
                      {showEmailTooltip && (
                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 bg-gray-700 text-white text-xs rounded px-3 py-2 shadow-lg z-10 w-[165px]">
                          Click on the below Email Id to compose an email.
                        </div>
                      )}
                      </span>
                    </div>
                    <a
                      href={`mailto:${paymentInfo.email}`}
                      className="text-red-600 hover:text-red-700 font-medium break-all"
                    >
                      {paymentInfo.email}
                    </a>
                  </div>
                </div>
                
                <div className="text-sm text-blue-700 mt-4 flex">
                  💡 
                  <p className="ml-2">
                    <strong>Note: </strong> 
                    Include your name and any specific purpose for the donation in your message. 
                    The receipt will be given to you once the payment has been confirmed.
                  </p>
                </div>
              </div>
            )}

            {/* Thank You Message */}
            {!initialLoading && (paymentInfo.zelleEmail || paymentInfo.venmoNumber) && (
              <div className="text-center bg-amber-50 p-[1.2rem] rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You for Your Generosity</h3>
                <p className="text-gray-600">
                  Your donations help us continue our mission and serve our community. 
                  May God bless you abundantly for your faithful giving.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Your Donation Matters</h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Community Outreach</h3>
              <p className="text-gray-600">
                Supporting local families in need, food drives, and community assistance programs.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Youth Programs</h3>
              <p className="text-gray-600">
                Funding Sunday school, youth fellowship activities, and educational resources.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Church Maintenance</h3>
              <p className="text-gray-600">
                Maintaining our beautiful church building and facilities for worship and gatherings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Dialog Modal */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
            <div className="flex items-center mb-4">
              {dialogType === 'success' ? (
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {dialogType === 'success' ? 'Success' : 'Error'}
              </h3>
            </div>
            <p className="text-gray-700 mb-6">{dialogMessage}</p>
            <div className="flex justify-end">
              <button
                onClick={closeDialog}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 z-50 transition-all duration-300 ease-in-out translate-y-0 opacity-100">
          <Check className="w-5 h-5" />
          <span className="font-medium">Copied to clipboard</span>
        </div>
      )}

    </div>
  );
};

export default Donate;